import { useEffect, useState } from "react"
import { Eye, EyeOff, Lock, Phone, RefreshCcw, ShieldCheck, X } from "lucide-react"
import { AnimatePresence, motion } from "framer-motion"
import { requestPasswordResetOTP, resetPasswordWithOTP } from "../../api/auth/post"

type CountryCode = {
  code: string
  country: string
  flag: string
}

type Props = {
  isOpen: boolean
  countryCode: string
  defaultPhone?: string
  countryCodes: CountryCode[]
  onResetSuccess?: (credentials: {
    phone: string
    password: string
    countryCode: string
    localPhone: string
  }) => Promise<void>
  onClose: () => void
}

export default function ForgotPasswordModal({
  isOpen,
  countryCode,
  defaultPhone = "",
  countryCodes,
  onResetSuccess,
  onClose,
}: Props) {
  const [selectedCountryCode, setSelectedCountryCode] = useState(countryCode)
  const [phone, setPhone] = useState(defaultPhone)
  const [otp, setOtp] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [step, setStep] = useState<"phone" | "reset">("phone")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setSelectedCountryCode(countryCode)
      setPhone(defaultPhone)
      setOtp("")
      setPassword("")
      setConfirmPassword("")
      setStep("phone")
      setLoading(false)
      setError("")
      setSuccess("")
      setShowPassword(false)
      setShowConfirmPassword(false)
    }
  }, [countryCode, defaultPhone, isOpen])

  const fullPhone = `${selectedCountryCode}${phone}`.trim()

  const handleRequestCode = async () => {
    if (!phone.trim()) {
      setError("Veuillez saisir votre numéro de téléphone")
      return
    }

    setLoading(true)
    setError("")
    setSuccess("")

    try {
      await requestPasswordResetOTP(fullPhone)
      setStep("reset")
      setSuccess("Un code de vérification a été envoyé à ce numéro.")
    } catch (err: any) {
      setError(err?.message || "Impossible d'envoyer le code pour le moment")
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async () => {
    if (otp.trim().length !== 4) {
      setError("Le code OTP doit contenir 4 chiffres")
      return
    }

    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères")
      return
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas")
      return
    }

    setLoading(true)
    setError("")
    setSuccess("")

    try {
      const minimumLoadingTime = new Promise((resolve) => setTimeout(resolve, 2000))
      const resetPromise = resetPasswordWithOTP({ phone: fullPhone, otp, password })

      await Promise.all([resetPromise, minimumLoadingTime])
      setSuccess("Mot de passe mis à jour. Connexion en cours...")

      if (onResetSuccess) {
        await onResetSuccess({
          phone: fullPhone,
          password,
          countryCode: selectedCountryCode,
          localPhone: phone,
        })
      } else {
        setTimeout(() => {
          onClose()
        }, 1200)
      }
    } catch (err: any) {
      setError(err?.message || "Réinitialisation impossible. Vérifiez le code reçu.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl"
            initial={{ scale: 0.94, y: 24 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.94, y: 24 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Mot de passe oublié</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {step === "phone"
                    ? "Recevez un code OTP pour réinitialiser votre accès."
                    : "Saisissez le code reçu puis définissez un nouveau mot de passe."}
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-6 space-y-4">
              {error && (
                <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              {success && (
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  {success}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Numéro de téléphone</label>
                <div className="flex gap-2">
                  <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3">
                    <span className="text-lg leading-none">
                      {(countryCodes.find((c) => c.code === selectedCountryCode) || countryCodes[0]).flag}
                    </span>
                    <select
                      value={selectedCountryCode}
                      onChange={(e) => setSelectedCountryCode(e.target.value)}
                      className="border-none bg-transparent text-sm font-semibold text-slate-700 outline-none focus:ring-0 focus:outline-none"
                    >
                      {countryCodes.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </select>
                  </div>

                  <Input
                    icon={<Phone size={18} />}
                    value={phone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                    placeholder="07 00 00 00 00"
                    inputMode="numeric"
                    autoComplete="tel"
                    disabled={loading || step === "reset"}
                  />
                </div>
              </div>

              {step === "reset" && (
                <>
                  <Input
                    icon={<ShieldCheck size={18} />}
                    value={otp}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))
                    }
                    placeholder="Code OTP"
                    inputMode="numeric"
                    maxLength={4}
                    disabled={loading}
                  />

                  <Input
                    icon={<Lock size={18} />}
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    placeholder="Nouveau mot de passe"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    disabled={loading}
                    trailing={
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="text-slate-400 transition hover:text-slate-600"
                        aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    }
                  />

                  <Input
                    icon={<Lock size={18} />}
                    value={confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                    placeholder="Confirmer le mot de passe"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    disabled={loading}
                    trailing={
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="text-slate-400 transition hover:text-slate-600"
                        aria-label={showConfirmPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    }
                  />
                </>
              )}
            </div>

            <div className="mt-6 space-y-3">
              {step === "phone" ? (
                <button
                  type="button"
                  onClick={handleRequestCode}
                  disabled={loading}
                  className="w-full rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {loading ? "Envoi..." : "Envoyer le code"}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={loading}
                    className="w-full rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {loading ? "Réinitialisation..." : "Réinitialiser le mot de passe"}
                  </button>

                  <button
                    type="button"
                    onClick={handleRequestCode}
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    <RefreshCcw size={14} />
                    Renvoyer le code
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function Input({ icon, trailing, className = "", ...props }: any) {
  return (
    <div className={`relative flex-1 ${className}`}>
      <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{icon}</div>
      <input
        {...props}
        className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-10 text-[15px] text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-300 focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50"
      />
      {trailing && <div className="absolute right-3 top-1/2 -translate-y-1/2">{trailing}</div>}
    </div>
  )
}
